import MainLayout from "../../components/layouts/MainLayout";
import { Link, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { Alert, Spin, Collapse, Card, Tag, Popconfirm, Button, message } from "antd";
import { PlusCircleOutlined, LeftCircleOutlined, CheckCircleOutlined, DeleteFilled } from "@ant-design/icons";
import { formationApi } from "../../api/formationApi";

const { Panel } = Collapse;

const FormationDetails = () => {
    const { id } = useParams();
    const [formation, setFormation] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [deletingMediaId, setDeletingMediaId] = useState(null);
    const abortController = new AbortController();

    const handleDeleteMedia = async (mediaId) => {
        setDeletingMediaId(mediaId);
        try {
            const res = await formationApi.deleteFormationMedia(mediaId, abortController.signal);
            if (!res.success) {
                message.error(res.message);
                return;
            }
            message.success(res.message);
            setFormation((prev) => ({
                ...prev,
                medias: prev.medias.filter((media) => media.id !== mediaId),
            }));
        } catch (error) {
            message.error("Échec de la suppression de l'image");
        } finally {
            setDeletingMediaId(null);
        }
    };

    const formatDate = (date) => {
        if (!date) return "Non fourni";
        return new Date(date).toLocaleDateString("fr-FR", {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await formationApi.getFormationDetails(`${id}`, abortController.signal);
            setFormation(res.data);
        } catch (err) {
            setError("Impossible de charger les détails de la formation.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [id]);

    return (
        <MainLayout>
            <div className="container mx-auto p-6">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-3xl font-semibold">Détails de la formation</h1>
                    <Link to={`/formations`} className="flex items-center text-blue-600 hover:text-blue-800">
                        <LeftCircleOutlined className="mr-2" />
                        Retour
                    </Link>
                </div>

                {loading && <Spin size="large" />}
                {error && <Alert message={error} type="error" />}
                {!loading && !error && formation && (
                    <div className="bg-white p-6 rounded-lg shadow-md">
                        {/* Image principale en en-tête */}
                        {formation.main_image && (
                            <div className="mb-6">
                                <img
                                    src={formation.main_image}
                                    alt={formation.title || "Image principale"}
                                    className="w-full h-96 object-cover rounded-lg border border-gray-300"
                                />
                            </div>
                        )}

                        {/* Informations de base */}
                        <Collapse defaultActiveKey={["1"]} expandIconPosition="start">
                            <Panel
                                header="Informations de la formation"
                                key="1"
                                extra={<PlusCircleOutlined />}
                            >
                                <div className="space-y-4">
                                    <p>
                                        <strong>Titre de la formation:</strong> {formation.title}
                                    </p>
                                    
                                    <p>
                                        <strong>Catégorie :</strong>{" "}
                                        {formation?.category?.libelle || "Non fourni"}
                                    </p>
                                    
                                    <p>
                                        <strong>Date publication :</strong>{" "}
                                        {formatDate(formation?.created_at)}
                                    </p>
                                    <p>
                                        <strong>Formation premium :</strong>{" "}
                                        {formation?.is_premium === 1 ? (
                                            <Tag color="green">OUI</Tag>
                                        ) : (
                                            <Tag color="default">NON</Tag>
                                        )}
                                    </p>
                                </div>
                            </Panel>
                        </Collapse>

                        {/* Contenu de la formation */}
                        {formation.content && (
                            <Collapse defaultActiveKey={["2"]} expandIconPosition="start" className="mt-4">
                                <Panel
                                    header="Contenu de la formation"
                                    key="2"
                                    extra={<PlusCircleOutlined />}
                                >
                                    <div 
                                        className="prose max-w-none"
                                        dangerouslySetInnerHTML={{ __html: formation.content }}
                                    />
                                </Panel>
                            </Collapse>
                        )}

                        {/* Description */}
                        {formation.description && (
                            <Collapse defaultActiveKey={["3"]} expandIconPosition="start" className="mt-4">
                                <Panel
                                    header="Description"
                                    key="3"
                                    extra={<PlusCircleOutlined />}
                                >
                                    <div className="space-y-4">
                                        <p>{formation.description}</p>
                                    </div>
                                </Panel>
                            </Collapse>
                        )}

                        {/* Quiz Section */}
                        {formation?.is_training === 1 && (
                            <Collapse
                                defaultActiveKey={["4"]}
                                expandIconPosition="start"
                                className="mt-4"
                            >
                                <Panel
                                header="Quiz associé"
                                key="4"
                                extra={<PlusCircleOutlined />}
                                >
                                {formation?.quiz?.questions?.length > 0 ? (
                                    <div className="space-y-6">
                                    {formation?.quiz?.questions?.map((quizItem, questionIndex) => (
                                        <Card
                                        key={questionIndex}
                                        title={`Question ${questionIndex + 1}`}
                                        className="mb-4"
                                        >
                                        <p className="text-lg font-semibold mb-4">
                                            {quizItem.libelle}
                                        </p>
                                        <div className="space-y-2">
                                            {quizItem.answers &&
                                            quizItem?.answers?.map((answer, answerIndex) => (
                                                <div
                                                key={answerIndex}
                                                className={`p-3 rounded-md border-2 ${
                                                    answer.is_true === 1
                                                    ? "bg-green-50 border-green-500"
                                                    : "bg-gray-50 border-gray-200"
                                                }`}
                                                >
                                                <div className="flex items-center justify-between">
                                                    <span
                                                    className={
                                                        answer.is_true === 1
                                                        ? "font-semibold text-green-700"
                                                        : "text-gray-700"
                                                    }
                                                    >
                                                    {answer.libelle}
                                                    </span>
                                                    {answer.is_true === 1 && (
                                                    <Tag
                                                        color="success"
                                                        icon={<CheckCircleOutlined />}
                                                    >
                                                        Bonne réponse
                                                    </Tag>
                                                    )}
                                                </div>
                                                </div>
                                            ))}
                                        </div>
                                        </Card>
                                    ))}
                                    </div>
                                ) : (
                                    <p className="text-gray-500 italic">
                                    Aucun quiz associé à cette formation.
                                    </p>
                                )}
                                </Panel>
                            </Collapse>
                            )}

                        {/* Auteur */}
                        {formation?.user && (
                            <Collapse
                                defaultActiveKey={["5"]}
                                expandIconPosition="start"
                                className="mt-4"
                            >
                                <Panel
                                    header="Auteur de la publication"
                                    key="5"
                                    extra={<PlusCircleOutlined />}
                                >
                                    <div className="space-y-4">
                                        <p>
                                            <strong>Nom complet :</strong>{" "}
                                            {formation?.user?.name} {formation?.user?.lastname}
                                        </p>
                                        <p>
                                            <strong>Contact :</strong> {formation?.user?.contact || "Non fourni"}
                                        </p>
                                    </div>
                                </Panel>
                            </Collapse>
                        )}

                        {/* Images */}
                        {formation?.medias && formation.medias.filter((img) => img?.etat === 1).length > 0 && (
                            <Collapse
                                defaultActiveKey={["6"]}
                                expandIconPosition="start"
                                className="mt-4"
                            >
                                <Panel header="Images supplémentaires" key="6" extra={<PlusCircleOutlined />}>
                                    <div className="flex flex-wrap gap-4">
                                        {formation.medias
                                            .filter((image) => image?.etat === 1)
                                            .map((image, index) => (
                                                <div
                                                    key={index}
                                                    className="relative w-32 h-32 border border-gray-300 rounded-md overflow-hidden"
                                                >
                                                    <img
                                                        src={image?.file_path}
                                                        alt={formation?.title || "Image"}
                                                        className="w-full h-full object-cover"
                                                    />
                                                    <Popconfirm
                                                        title="Supprimer cette image ?"
                                                        onConfirm={() => handleDeleteMedia(image.id)}
                                                        okText="Oui"
                                                        cancelText="Non"
                                                    >
                                                        <Button
                                                            danger
                                                            size="small"
                                                            shape="circle"
                                                            icon={<DeleteFilled />}
                                                            loading={deletingMediaId === image.id}
                                                            className="absolute top-1 right-1"
                                                        />
                                                    </Popconfirm>
                                                </div>
                                            ))}
                                    </div>
                                </Panel>
                            </Collapse>
                        )}
                    </div>
                )}
            </div>
        </MainLayout>
    );
};

export default FormationDetails;
